import { useEffect, useState, ChangeEvent, MouseEvent, FC } from 'react';
import { Box, Button, FormLabel, Grid, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Edit, Info } from '@mui/icons-material';
import axios from 'axios';
import { Accordion, Loader } from '../components';
import { IModule, IRole } from './type';
import getAuthHeader from '../libs/authHeader';

const initialState = {
  name: '',
};

const Role: FC = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const roleId = queryParams.get('id') || '';
  const [role, setRole] = useState<IRole>(initialState);
  const [modules, setModules] = useState<IModule[]>([]);
  const [isNameEmpty, setIsNameEmpty] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingPermissionUpdates, setPendingPermissionUpdates] = useState<any[]>([]);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const customStyles = {
    width: '100%',
    overflow: 'hidden',
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '8px !important',
    fontWeight: 500,
  };
  useEffect(() => {
    const permissions = ['Read', 'Write', 'Edit', 'Delete'];
    let permissionObj = {};
    permissions.forEach((per) => {
      permissionObj = {
        ...permissionObj,
        [per]: false,
      };
    });
    const fetchRoleDetails = async () => {
      if (roleId) {
        try {
          const {
            data: { data },
          } = await axios.get(`${process.env.REACT_APP_API_URL}/role/${roleId}`, getAuthHeader());
          const { module } = data;
          setRole(data);
          setModules(module);
        } catch (error) {
          // Error fetching role details
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchRoleDetails();
    if (!roleId) {
      const fetchModules = async () => {
        try {
          const { data: { data = {} } = {} } = await axios.get(`${process.env.REACT_APP_API_URL}/module/getSubModules`, getAuthHeader());
          const Module = data.map((module: any) => ({
            id: module.id,
            module: module.name,
            code: module.code,
            permissions: { ...permissionObj },
            subModules: module.subModule.map((m: any) => ({
              id: m.id,
              subModule: m.name,
              code: m.code,
              permissions: { ...permissionObj },
            })),
          }));
          setModules((m) => [...m, ...Module]);
        } catch (error) {
          // Error fetching modules
        } finally {
          setIsLoading(false);
        }
      };
      fetchModules();
    }
  }, [roleId]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRole({
      ...role,
      [name]: value,
    });
  };
  const handleBack = () => {
    navigate('/roles?param=0');
  };

  const updateRoleName = async () => {
    setIsEditing((prev) => !prev);
    try {
      const payload = {
        id: parseInt(roleId, 10),
        name: role?.name || '',
      };
      await axios.put(`${process.env.REACT_APP_API_URL}/role/updateName`, payload, getAuthHeader());
    } catch (error) {
      // Error updating permission
    }
  };

  const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>, mode: string, moduleId: number, subModuleId?: number) => {
    const { name, checked } = event.target;

    const permissionMap: Record<string, number> = {
      Read: 1,
      Write: 2,
      Edit: 3,
      Delete: 4,
    };

    const updatedState = modules.map((feature: any) => {
      if (mode === 'modules' && feature.id === moduleId) {
        const updatedFeature = {
          ...feature,
          permissions: {
            ...feature.permissions,
            [name]: checked,
          },
        };

        // If any permission is being enabled for a module, automatically enable the same permission for all its submodules
        if (checked) {
          updatedFeature.subModules = feature.subModules.map((sub: any) => ({
            ...sub,
            permissions: {
              ...sub.permissions,
              [name]: true,
            },
          }));
        }

        return updatedFeature;
      }

      if (mode === 'subModules') {
        return {
          ...feature,
          subModules: feature.subModules.map((sub: any) => {
            if (feature.id === moduleId && sub.id === subModuleId) {
              return {
                ...sub,
                permissions: {
                  ...sub.permissions,
                  [name]: checked,
                },
              };
            }
            return sub;
          }),
        };
      }

      return feature;
    });

    setModules(updatedState);

    // Create permission updates for the main module/submodule change
    const permissionUpdate = {
      roleId: parseInt(roleId, 10),
      moduleId: mode === 'modules' ? moduleId : undefined,
      subModuleId: mode === 'subModules' ? subModuleId : undefined,
      permissionId: permissionMap[name],
      permission: checked,
    };

    // If any permission is being enabled for a module, also create updates for all submodules
    const updatesToAdd = [permissionUpdate];

    if (mode === 'modules' && checked) {
      const module = modules.find((feature: any) => feature.id === moduleId);
      if (module) {
        module.subModules.forEach((subModule: any) => {
          updatesToAdd.push({
            roleId: parseInt(roleId, 10),
            moduleId: undefined,
            subModuleId: subModule.id,
            permissionId: permissionMap[name],
            permission: true,
          });
        });
      }
    }

    // Add to pending updates instead of making immediate API call
    setPendingPermissionUpdates((prev) => {
      const newUpdates = [...prev];

      updatesToAdd.forEach((update) => {
        const existingIndex = newUpdates.findIndex((existingUpdate) => existingUpdate.moduleId === update.moduleId && existingUpdate.subModuleId === update.subModuleId && existingUpdate.permissionId === update.permissionId);

        if (existingIndex >= 0) {
          // Update existing permission
          newUpdates[existingIndex] = update;
        } else {
          // Add new permission update
          newUpdates.push(update);
        }
      });

      return newUpdates;
    });
  };

  const savePermissions = async () => {
    if (pendingPermissionUpdates.length === 0) {
      return;
    }

    setIsSavingPermissions(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/role/update`,
        {
          updates: pendingPermissionUpdates,
        },
        getAuthHeader(),
      );

      // Clear pending updates after successful save
      setPendingPermissionUpdates([]);
    } catch (error) {
      // Error updating permissions
      // Optional: Show error message or toast
    } finally {
      setIsSavingPermissions(false);
    }
  };

  const handleSubmit = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!role.name.trim()) {
      setIsNameEmpty(true);
      return;
    }
    setIsNameEmpty(false);
    let formData;
    if (roleId) {
      const { name } = role;
      formData = {
        id: roleId,
        name,
      };
    } else {
      formData = {
        ...role,
      };
    }
    if (roleId) {
      try {
        const { data } = await axios.put(`${process.env.REACT_APP_API_URL}/role/`, formData, getAuthHeader());
        if (data.error) {
          navigate('/roles?param=3');
        } else if (data.message === 'No matching record found') {
          navigate('/roles?param=4');
        } else {
          navigate('/roles?param=2');
        }
      } catch (error) {
        // Error updating role
      }
    } else {
      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/role`, formData, getAuthHeader());
        if (data.error) {
          navigate('/roles?param=3');
        } else {
          navigate(`/roles/add?id=${data?.data?.id}`);
        }
      } catch (error) {
        // Error creating role
      }
    }
  };
  return (
    <>
      <Loader open={isLoading} />
      <Grid item lg={12} xs={12} sm={12}>
        <Paper sx={customStyles}>
          <Box mt={2} sx={{ background: 'white' }} p={4}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">{roleId ? 'Update Role' : 'Add Role'}</Typography>
            </Stack>
            {!roleId && (
              <Stack direction="column" spacing={2} my={2}>
                <Stack direction="row" alignItems="center">
                  <FormLabel
                    sx={{
                      fontSize: '1rem',
                      fontWeight: '900',
                      m: '0',
                      color: '#000',
                    }}
                    required
                  >
                    Name
                  </FormLabel>
                  <Tooltip title="Name is required" sx={{ marginLeft: '0.5rem' }}>
                    <Info />
                  </Tooltip>
                </Stack>
                <TextField name="name" id="outlined-basic" variant="outlined" onChange={handleChange} value={role?.name} required inputProps={{ maxLength: 30 }} />
                {isNameEmpty && (
                  <Typography variant="body2" color="error">
                    Name is required
                  </Typography>
                )}
              </Stack>
            )}
            {roleId && (
              <Stack direction="column" spacing={2} my={2}>
                <Stack direction="row" alignItems="center">
                  <FormLabel
                    sx={{
                      fontSize: '1rem',
                      fontWeight: '900',
                      m: '0',
                      color: '#000',
                    }}
                    required
                  >
                    Name
                  </FormLabel>
                  <Tooltip title="Name is required" sx={{ marginLeft: '0.5rem' }}>
                    <Info />
                  </Tooltip>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1}>
                  {role && isEditing ? (
                    <TextField name="name" id="outlined-basic" variant="outlined" onChange={handleChange} value={role?.name} required inputProps={{ maxLength: 30 }} autoFocus />
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {role?.name || ''}
                    </Typography>
                  )}
                  {roleId &&
                    (isEditing ? (
                      <Button className="text-capitalize" variant="contained" onClick={updateRoleName}>
                        Save
                      </Button>
                    ) : (
                      <Edit sx={{ cursor: 'pointer', color: 'black' }} onClick={() => setIsEditing(true)} />
                    ))}
                </Stack>
                {isNameEmpty && (
                  <Typography variant="body2" color="error">
                    Name is required
                  </Typography>
                )}
              </Stack>
            )}
            {roleId && (
              <Stack direction="column" spacing={2} mt={4}>
                <Typography variant="body1" sx={{ fontWeight: '900' }}>
                  Update Permissions
                </Typography>
                <Accordion modules={modules} onHandleSwitchChange={handleSwitchChange} />
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between" mt={4}>
              <Button variant="contained" onClick={handleBack}>
                Back
              </Button>
              {roleId ? (
                <Button variant="contained" color="primary" onClick={savePermissions} disabled={isSavingPermissions || pendingPermissionUpdates.length === 0}>
                  {isSavingPermissions ? 'Saving...' : 'Save Permissions'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleSubmit}>
                  Add Permissions
                </Button>
              )}
            </Stack>
          </Box>
        </Paper>
      </Grid>
    </>
  );
};

export default Role;
